/**
 * TrainingPOSPage - Isolated Training POS Terminal
 * 
 * This is a fully sandboxed POS environment for staff training.
 * - All orders are marked as "training" and do NOT affect:
 *   - Real inventory (no ingredient deductions)
 *   - Financial reports (no sales recorded)
 *   - Shift reconciliation (no cash tracking)
 *   - FRCS fiscal invoices (training invoices only)
 * 
 * URL: /training-pos
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Coffee, X, Sun, Moon, LogOut, GraduationCap, Check, QrCode, 
  Printer, AlertCircle, Loader2, ChevronDown, ChevronUp, Bell, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';
import { useTheme } from '@/context/ThemeContext';
import ItemCustomizationModal from '@/components/ordering/ItemCustomizationModal';

// Import refactored sub-components
import {
  POSPinPad,
  POSCustomerLookup,
  POSNewCustomerForm,
  POSCart,
  POSMenuGrid,
  POSQuickActions,
  POSOrderTypeSelector
} from '@/components/pos';
import POSMobileCartDrawer from '@/components/pos/POSMobileCartDrawer';

// Training Mode Top Bar with permanent banner
function TrainingTopBar({ staff, onLogout, onToggleTheme, theme }) {
  return (
    <>
      {/* Permanent Training Banner */}
      <div className="bg-amber-500 text-white text-center py-3 px-4 flex items-center justify-center gap-3 shadow-lg">
        <AlertTriangle className="w-6 h-6" />
        <span className="font-bold text-lg tracking-wide">TRAINING MODE</span>
        <span className="text-amber-100 text-sm">Orders are simulated - NO real transactions</span>
        <AlertTriangle className="w-6 h-6" />
      </div>
      
      <div className="bg-white dark:bg-gray-800 border-b-4 border-amber-500 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <GraduationCap className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <span className="font-semibold text-gray-900 dark:text-white">Training POS</span>
            <p className="text-xs text-amber-600 dark:text-amber-400">Practice Environment</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Trainee: <span className="font-medium text-gray-900 dark:text-white">{staff?.name}</span>
          </span>
          <Button variant="ghost" size="sm" onClick={onToggleTheme} className="text-gray-500">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={onLogout} className="text-red-500" data-testid="training-pos-logout-btn">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </>
  );
}

// Training Order Complete Screen
function TrainingOrderCompleteScreen({ orderComplete, onStartNewOrder }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-800 to-amber-900 flex items-center justify-center p-8">
      {/* Watermark */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none opacity-10">
        <span className="text-[200px] font-bold text-white rotate-[-30deg]">TRAINING</span>
      </div>
      
      <div className="text-center text-white relative z-10">
        <div className="w-32 h-32 mx-auto mb-8 rounded-full bg-amber-500 flex items-center justify-center animate-bounce">
          <GraduationCap className="w-16 h-16" />
        </div>
        <h1 className="text-5xl font-bold mb-4">Training Order Complete!</h1>
        
        <div className="bg-amber-600/50 border-2 border-amber-400 rounded-xl px-6 py-4 mb-6 inline-block">
          <div className="flex items-center gap-2 text-amber-200 mb-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-bold">THIS IS NOT A REAL ORDER</span>
          </div>
          <p className="text-sm text-amber-100">No inventory was deducted. No sales were recorded.</p>
        </div>
        
        <div className="bg-white/10 rounded-3xl p-8 inline-block mb-6">
          <div className="text-lg text-amber-200 mb-2">Simulated Order #</div>
          <div className="text-6xl font-bold">{orderComplete.order_number}</div>
        </div>
        <p className="text-2xl mb-6">Practice Total: ${orderComplete.total.toFixed(2)}</p>
        
        <Button 
          onClick={onStartNewOrder}
          className="w-full max-w-md py-6 text-xl bg-amber-500 hover:bg-amber-600"
          data-testid="training-new-order-btn"
        >
          <GraduationCap className="w-6 h-6 mr-2" />
          Practice Another Order
        </Button>
      </div>
    </div>
  );
}

// Main Training POS Terminal Component
export default function TrainingPOSPage() {
  const { isDarkMode, toggleDarkMode } = useTheme();
  
  // Auth State
  const [staff, setStaff] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  
  // View State
  const [view, setView] = useState('login');
  const [customer, setCustomer] = useState(null);
  const [customerDiscount, setCustomerDiscount] = useState(null);
  
  // Menu & Cart State
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Payment State
  const [showPayment, setShowPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [orderProcessing, setOrderProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(null);
  
  // Customization modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCustomization, setShowCustomization] = useState(false);
  const [availableAddOns, setAvailableAddOns] = useState([]);
  
  // Mobile cart drawer state
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  
  // Upsell reminder state
  const [upsellReminderCollapsed, setUpsellReminderCollapsed] = useState(false);

  // Order Type State
  const [orderType, setOrderType] = useState('takeaway');
  const [tableNumber, setTableNumber] = useState(null);
  const [availableTables, setAvailableTables] = useState([]);

  // Handle PIN login
  const handleLogin = async (pin) => {
    setLoginLoading(true);
    try {
      const res = await api.post('/api/device/login', { pin, device_type: 'training' });
      setStaff(res.data.staff);
      setView('customer-lookup');
      toast.success(`Training mode activated for ${res.data.staff.name}`);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Invalid PIN' };
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    setStaff(null);
    setCustomer(null);
    setCustomerDiscount(null);
    setCart([]);
    setView('login');
    toast.info('Logged out of training mode');
  };

  // Fetch menu items
  const fetchMenu = useCallback(async () => {
    try {
      setLoading(true);
      const cacheBuster = `?_t=${Date.now()}`;
      const res = await api.get(`/menu${cacheBuster}`);
      const available = res.data.filter(item => item.is_available !== false);
      setMenuItems(available);
      const cats = [...new Set(available.map(item => item.category))].filter(Boolean);
      setCategories(cats);
      
      const addOnsRes = await api.get(`/add-ons${cacheBuster}`);
      setAvailableAddOns(addOnsRes.data.filter(a => a.is_available !== false));
    } catch (err) {
      console.error('Failed to fetch menu:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch tables for dine-in
  const fetchTables = useCallback(async () => {
    try {
      const res = await api.get('/tables/active');
      setAvailableTables(res.data.tables || []);
    } catch (err) {
      console.error('Failed to fetch tables:', err);
    }
  }, []);

  useEffect(() => {
    if (staff) {
      fetchTables();
    }
  }, [staff, fetchTables]);

  // Select customer (simulated for training)
  const handleSelectCustomer = (cust) => {
    setCustomer(cust);
    // Simulate discount for training
    if (cust?.email) {
      setCustomerDiscount({ discount: 10, tier: 'Training Discount' });
      toast.info('Training: 10% simulated loyalty discount applied');
    }
    setView('ordering');
  };

  // Skip customer lookup (guest order)
  const handleSkipCustomer = () => {
    setCustomer(null);
    setCustomerDiscount(null);
    setView('ordering');
  };

  // Fetch menu on mount
  useEffect(() => {
    if (view === 'ordering' && menuItems.length === 0) {
      fetchMenu();
    }
  }, [view, menuItems.length, fetchMenu]);

  // Add item to cart
  const handleItemClick = (item) => {
    if (orderType === 'dine-in' && !tableNumber) {
      toast.error('Please select a table number first');
      return;
    }
    
    if (item.has_customizations || item.sizes?.length > 1 || availableAddOns.length > 0) {
      setSelectedItem(item);
      setShowCustomization(true);
    } else {
      addToCart(item);
    }
  };

  const addToCart = (item, customizations = null, selectedSize = null) => {
    // Use BASE item.price as default - sizes are upsells, only apply if explicitly selected
    const itemPrice = selectedSize?.price || item.price || item.sizes?.[0]?.price || 0;
    const customizationsTotal = customizations?.reduce((sum, c) => sum + (c.price || 0), 0) || 0;
    const totalPrice = itemPrice + customizationsTotal;
    
    const cartItem = {
      item_id: item.id + (selectedSize ? `-${selectedSize.id}` : '') + (customizations ? `-${Date.now()}` : ''),
      name: item.name + (selectedSize ? ` (${selectedSize.name})` : ''),
      price: totalPrice,
      quantity: 1,
      customizations: customizations || [],
      size: selectedSize,
      image: item.image,
      category: item.category
    };
    
    setCart(prev => {
      const existing = prev.find(c => c.item_id === cartItem.item_id && !customizations);
      if (existing && !customizations) {
        return prev.map(c => c.item_id === cartItem.item_id ? { ...c, quantity: c.quantity + 1 } : c);
      }
      return [...prev, cartItem];
    });
    
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    
    toast.success(`Added ${item.name} (Training)`);
  };

  // Update quantity
  const updateQuantity = (itemId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.item_id === itemId) {
        const newQty = item.quantity + delta;
        return newQty <= 0 ? null : { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  // Remove item
  const removeItem = (itemId) => {
    setCart(prev => prev.filter(item => item.item_id !== itemId));
  };

  // Clear cart
  const clearCart = () => {
    setCart([]);
  };

  // Process TRAINING order (no real impact)
  const processOrder = async () => {
    if (cart.length === 0) return;
    
    setOrderProcessing(true);
    try {
      // Calculate totals (same as real POS for training consistency)
      const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const discountPercent = customerDiscount?.discount || 0;
      const discountAmount = subtotal * (discountPercent / 100);
      const total = subtotal - discountAmount;
      
      // Create a TRAINING order that doesn't affect inventory/finances
      const orderData = {
        items: cart.map(item => ({
          item_id: item.item_id.split('-')[0],
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          customizations: item.customizations,
          size: item.size
        })),
        customer_id: customer?.contact_id || customer?.id,
        customer_email: customer?.email,
        customer_name: customer?.name || customer?.first_name,
        subtotal,
        discount_percent: discountPercent,
        discount_amount: discountAmount,
        total,
        payment_method: paymentMethod,
        staff_id: staff?.id,
        staff_name: staff?.name,
        order_type: 'training',  // Mark as training
        is_training: true,       // Flag to skip inventory deduction
        invoice_type: 'TS'       // Training Sale
      };
      
      const res = await api.post('/api/pos/orders', orderData);
      const data = res.data;
      
      setOrderComplete({
        order_number: data.order_number || 'TRN-' + Date.now().toString().slice(-6),
        total,
        is_training: true
      });
      
      setCart([]);
      setShowPayment(false);
      
      toast.success('Training order completed! (No real transaction recorded)');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Training order failed');
    } finally {
      setOrderProcessing(false);
    }
  };

  // Start new order
  const startNewOrder = () => {
    setOrderComplete(null);
    setCustomer(null);
    setCustomerDiscount(null);
    setOrderType('takeaway');
    setTableNumber(null);
    setView('customer-lookup');
  };

  // Render based on view state
  if (view === 'login') {
    return (
      <div className="min-h-screen bg-amber-50 dark:bg-gray-900">
        {/* Training Banner on Login */}
        <div className="bg-amber-500 text-white text-center py-4 px-4">
          <div className="flex items-center justify-center gap-3 mb-2">
            <GraduationCap className="w-8 h-8" />
            <span className="font-bold text-2xl">Training POS</span>
            <GraduationCap className="w-8 h-8" />
          </div>
          <p className="text-amber-100">Practice taking orders without affecting real data</p>
        </div>
        <POSPinPad onLogin={handleLogin} loading={loginLoading} />
      </div>
    );
  }

  if (view === 'customer-lookup') {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
        <TrainingTopBar 
          staff={staff} 
          onLogout={handleLogout} 
          onToggleTheme={toggleDarkMode} 
          theme={isDarkMode ? 'dark' : 'light'}
        />
        <POSCustomerLookup
          onSelectCustomer={handleSelectCustomer}
          onNewCustomer={() => setView('new-customer')}
          onSkip={handleSkipCustomer}
        />
      </div>
    );
  }

  if (view === 'new-customer') {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
        <TrainingTopBar 
          staff={staff} 
          onLogout={handleLogout} 
          onToggleTheme={toggleDarkMode} 
          theme={isDarkMode ? 'dark' : 'light'}
        />
        <POSNewCustomerForm
          onSave={handleSelectCustomer}
          onCancel={() => setView('customer-lookup')}
        />
      </div>
    );
  }

  if (orderComplete) {
    return (
      <TrainingOrderCompleteScreen
        orderComplete={orderComplete}
        onStartNewOrder={startNewOrder}
      />
    );
  }

  // Main ordering view
  const categoryList = categories.map(c => ({ id: c, name: c, is_active: true }));

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex flex-col" data-testid="training-pos-ordering-screen">
      {/* Training Top Bar with permanent banner */}
      <TrainingTopBar 
        staff={staff} 
        onLogout={handleLogout} 
        onToggleTheme={toggleDarkMode} 
        theme={isDarkMode ? 'dark' : 'light'}
      />

      {/* Order Type Selector */}
      <POSOrderTypeSelector
        orderType={orderType}
        setOrderType={setOrderType}
        tableNumber={tableNumber}
        setTableNumber={setTableNumber}
        tables={availableTables}
        disabled={cart.length > 0}
      />

      {/* Customer Info Bar */}
      {customer && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-amber-700 dark:text-amber-300">
              Training Customer: <span className="font-medium">{customer.name || `${customer.first_name} ${customer.last_name || ''}`}</span>
            </span>
            {customerDiscount && (
              <span className="px-2 py-0.5 bg-amber-600 text-white text-xs rounded-full">
                {customerDiscount.discount}% {customerDiscount.tier}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setCustomer(null); setCustomerDiscount(null); setView('customer-lookup'); }}
            className="text-amber-600 dark:text-amber-400"
          >
            Change Customer
          </Button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Menu Grid */}
        <POSMenuGrid
          menuItems={menuItems}
          categories={categoryList}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          onItemClick={handleItemClick}
          search={search}
          setSearch={setSearch}
        />

        {/* Cart - Hidden on mobile */}
        <div className="hidden md:block">
          <POSCart
            cart={cart}
            customer={customer}
            customerDiscount={customerDiscount}
            appliedCashierDiscount={null}
            onUpdateQuantity={updateQuantity}
            onRemoveItem={removeItem}
            onClearCart={clearCart}
            onAddItem={addToCart}
            showPayment={showPayment}
            setShowPayment={setShowPayment}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            onProcessOrder={processOrder}
            orderProcessing={orderProcessing}
            onShowCashierDiscount={() => toast.info('Training: Cashier discount simulation')}
            onRemoveCashierDiscount={() => {}}
            upsellReminderCollapsed={upsellReminderCollapsed}
            setUpsellReminderCollapsed={setUpsellReminderCollapsed}
            orderType={orderType}
            tableNumber={tableNumber}
            staff={staff}
          />
        </div>

        {/* Mobile Cart Drawer */}
        <POSMobileCartDrawer
          isOpen={mobileCartOpen}
          onToggle={() => setMobileCartOpen(!mobileCartOpen)}
          itemCount={cart.reduce((sum, item) => sum + item.quantity, 0)}
          total={cart.reduce((sum, item) => sum + (item.price * item.quantity), 0)}
        >
          {mobileCartOpen && (
            <POSCart
              cart={cart}
              customer={customer}
              customerDiscount={customerDiscount}
              appliedCashierDiscount={null}
              onUpdateQuantity={updateQuantity}
              onRemoveItem={removeItem}
              onClearCart={clearCart}
              onAddItem={addToCart}
              showPayment={showPayment}
              setShowPayment={setShowPayment}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              onProcessOrder={processOrder}
              orderProcessing={orderProcessing}
              onShowCashierDiscount={() => {}}
              onRemoveCashierDiscount={() => {}}
              upsellReminderCollapsed={upsellReminderCollapsed}
              setUpsellReminderCollapsed={setUpsellReminderCollapsed}
              orderType={orderType}
              tableNumber={tableNumber}
              staff={staff}
              isMobileDrawer={true}
            />
          )}
        </POSMobileCartDrawer>
      </div>

      {/* Item Customization Modal */}
      {selectedItem && (
        <ItemCustomizationModal
          item={selectedItem}
          open={showCustomization}
          onOpenChange={(isOpen) => { 
            setShowCustomization(isOpen); 
            if (!isOpen) setSelectedItem(null); 
          }}
          onAddToCart={(cartItem) => {
            setCart(prev => {
              const existing = prev.find(c => c.item_id === cartItem.item_id && !cartItem.modifiers);
              if (existing && !cartItem.modifiers) {
                return prev.map(c => c.item_id === cartItem.item_id ? { ...c, quantity: c.quantity + cartItem.quantity } : c);
              }
              return [...prev, {
                ...cartItem,
                item_id: cartItem.item_id + (cartItem.selectedSize ? `-${cartItem.selectedSize}` : '') + (cartItem.modifiers ? `-${Date.now()}` : ''),
                name: cartItem.name + (cartItem.selectedSize ? ` (${cartItem.selectedSize})` : ''),
              }];
            });
            setShowCustomization(false);
            setSelectedItem(null);
          }}
          availableAddOns={availableAddOns}
        />
      )}
    </div>
  );
}
